import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import {
  IconSparkles, IconBolt, IconShieldCheck, IconUsers, IconChartBar, IconWorld, IconClockHour4, IconTicket,
} from '@tabler/icons-react';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { PageHero } from '@/components/marketing/warm/page-hero';
import { CtaBand } from '@/components/marketing/warm/cta-band';
import { AnswerCapsule } from '@/components/marketing/answer-capsule';
import { ContentFaq } from '@/components/marketing/content-faq';
import { Btn, Eyebrow, H2, IconTile, RevealSection } from '@/components/marketing/warm/primitives';
import {
  setDocumentMeta, setCanonical, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema,
} from '@/lib/seo';
import pagesJson from './landing-pages.json';

export type LandingCopy = {
  url: string;
  title: string;
  meta: string;
  label?: string;
  line?: string;
  eyebrow: string;
  h1: string;
  lead: string;
  answer: string;
  benefits: [string, string][];
  steps: [string, string][];
  links: [string, string][];
  faq: [string, string][];
  cta: [string, string];
};

export const LANDING_PAGES = pagesJson as unknown as LandingCopy[];

const BENEFIT_ICONS = [IconSparkles, IconBolt, IconShieldCheck, IconUsers, IconChartBar, IconWorld, IconClockHour4, IconTicket];

export function LandingPage({ page }: { page: LandingCopy }) {
  const faqs = page.faq.map(([q, a]) => ({ q, a }));

  useEffect(() => {
    setDocumentMeta(page.title, page.meta);
    setCanonical(page.url);
    // Breadcrumb parents (Solutions, Developers) have no page of their own, so the trail is Home > page.
    const trail = [{ name: 'Home', url: 'https://usekora.app' }, { name: page.h1, url: `https://usekora.app${page.url}` }];
    return injectJsonLdList([
      buildWebPageSchema(page.url, page.title, page.meta),
      buildBreadcrumbSchema(trail),
      buildFaqPageSchema(faqs),
    ]);
  }, [page, faqs]);

  return (
    <MarketingShell>
      <PageHero
        eyebrow={page.eyebrow}
        title={page.h1}
        lead={page.lead}
        actions={(
          <>
            <Btn to="/auth">Start free <ArrowRight className="size-3.5" /></Btn>
            <Link to="/pricing" className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm" style={{ color: 'var(--warm-muted)' }}>See pricing</Link>
          </>
        )}
      />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 -mt-6 md:-mt-10">
        <AnswerCapsule>{page.answer}</AnswerCapsule>
      </div>

      <RevealSection>
        <Eyebrow>What you get</Eyebrow>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {page.benefits.map(([title, body], i) => (
            <div key={title} className="rounded-2xl p-5" style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)' }}>
              <IconTile icon={BENEFIT_ICONS[i % BENEFIT_ICONS.length]} />
              <h3 className="mt-4 text-base font-medium" style={{ color: 'var(--warm-text)' }}>{title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>{body}</p>
            </div>
          ))}
        </div>
      </RevealSection>

      <RevealSection style={{ background: 'var(--warm-surface)' }}>
        <Eyebrow>How it works</Eyebrow>
        <H2>{page.steps.length} steps, no setup call.</H2>
        <ol className="mt-10 grid gap-4 md:grid-cols-2">
          {page.steps.map(([title, body], i) => (
            <li key={title} className="flex gap-4 rounded-2xl p-5" style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)' }}>
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-medium" style={{ background: 'var(--warm-inset)', color: 'var(--warm-text)' }}>{i + 1}</span>
              <span>
                <span className="block text-base font-medium" style={{ color: 'var(--warm-text)' }}>{title}</span>
                <span className="mt-1 block text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>{body}</span>
              </span>
            </li>
          ))}
        </ol>
      </RevealSection>

      <RevealSection style={{ background: 'var(--warm-surface)' }}>
        <Eyebrow>Related</Eyebrow>
        <ul className="grid gap-3 sm:grid-cols-2">
          {page.links.map(([to, line]) => (
            <li key={to}>
              <Link to={to} className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm" style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)', color: 'var(--warm-text)' }}>
                <span>{line}</span>
                <ArrowRight className="size-3.5 shrink-0" style={{ color: 'var(--warm-muted)' }} />
              </Link>
            </li>
          ))}
        </ul>
      </RevealSection>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-10">
        <ContentFaq faqs={faqs} />
      </div>

      <CtaBand title={page.cta[0]} lead={page.cta[1]} />
    </MarketingShell>
  );
}
